import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import printJS from 'print-js';
import {
  ArrowLeft,
  Check,
  Clock,
  Phone,
  Plus,
  Receipt,
  School,
  Search,
  Trash2,
  TriangleAlert,
  UserRound,
  Wallet,
  X,
} from 'lucide-react';
import { API_BASE_URL } from '../Config.js';
import PrintBillModal from '../print/PrintBillModal.js';
import { updateBillApi, getUpdateBillErrorMessage } from '../Api/updateBillApi.js';
import {
  BILL_EDIT_WINDOW_MINUTES,
  formatTimeLeft,
  getBillCreatedAt,
  getEditMsLeft,
  hasOnlyNewItems,
} from './billEditWindow.js';
import styles from './BillUpdateModal.module.css';

const CUSTOM_BARCODE = 'SG9999999';
const PAYMENT_MODES = ['Cash', 'Card', 'Upi', 'Due', 'Partial'];
const PAYMENT_LABELS = { Cash: 'Cash', Card: 'Card', Upi: 'UPI', Due: 'Due', Partial: 'Partial' };
const SEARCH_CATEGORY_LABELS = { ITEM_CODE: 'Item code', DESCRIPTION: 'Description' };
const MOBILE_PATTERN = /^[6-9]\d{9}$/;
const WARN_MS = 5 * 60 * 1000;

const toInt = (value) => {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? 0 : n;
};

const formatAmount = (value) =>
  Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const safeParse = (raw, fallback) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

// Same rounding as ItemService.getRoundedAmount (nearest 5)
const roundToFive = (value) => Math.round(value / 5) * 5;

// Line sell price with the same integer maths as the backend, so the preview
// matches the saved bill. A bill-level discount replaces item discounts.
const getSellPrice = (line, globalDiscount) => {
  if (globalDiscount > 0 || line.isCustom || line.discountAllowed === false) return line.price;
  const discount = toInt(line.discount);
  return discount > 0 ? line.price - Math.trunc((line.price * discount) / 100) : line.price;
};

// Cloth is sold by the metre: its line amount is rate × metres, not × quantity.
const getLineAmount = (line, globalDiscount) => {
  const sellPrice = getSellPrice(line, globalDiscount);
  const metres = parseFloat(line.itemSize);
  if ((line.itemType || '').toUpperCase() === 'CLOTH' && metres > 0) {
    return Math.round(sellPrice * metres);
  }
  return sellPrice * toInt(line.quantity);
};

let lineKeySeed = 0;
const nextLineKey = () => `line-${++lineKeySeed}`;

// Existing bill line -> editable row
const lineFromBillItem = (item, billHadGlobalDiscount) => {
  const isCustom = item.itemBarcodeID === CUSTOM_BARCODE;
  // With a bill-level discount every line stores that %, not its own.
  const lineDiscount = billHadGlobalDiscount ? 0 : toInt(item.discount);
  return {
    key: nextLineKey(),
    barcodeId: item.itemBarcodeID,
    description: item.description || '',
    itemType: item.itemType || '',
    itemColor: item.itemColor || '',
    itemCategory: item.itemCategory || '',
    itemSize: item.itemSize || '',
    price: toInt(item.price),
    discount: lineDiscount,
    // Unknown for existing lines unless a discount was already applied; the server re-checks.
    discountAllowed: isCustom ? false : lineDiscount > 0 ? true : null,
    quantity: Math.abs(toInt(item.quantity)) || 1,
    isCustom,
  };
};

// Inventory search result -> editable row
const lineFromInventoryItem = (item) => ({
  key: nextLineKey(),
  barcodeId: item.itemBarcodeID,
  description: item.description || '',
  itemType: item.itemType || '',
  itemColor: item.itemColor || '',
  itemCategory: item.itemCategory || '',
  itemSize: item.itemSize || '',
  price: toInt(item.price),
  discount: 0,
  discountAllowed: item.discount === 'Yes',
  quantity: 1,
  isCustom: false,
});

/**
 * BillUpdateModal
 * Edits a FRESH retail bill (customer, payment, items) inside its 20-minute
 * window, re-saves it through /user/update/billRequest and reprints it.
 *
 * Props:
 *  - bill      Billing object from the bill list (with its `bill` item lines)
 *  - onClose   () => void — dismiss without saving
 *  - onUpdated () => void — called after a successful update (and reprint)
 */
const BillUpdateModal = ({ bill, onClose, onUpdated }) => {
  const user = useMemo(() => safeParse(sessionStorage.getItem('user'), null), []);
  const token = sessionStorage.getItem('token');
  const storeId = bill?.storeId || user?.storeId || '';
  const billHadGlobalDiscount = toInt(bill?.discount) > 0;
  const createdAt = getBillCreatedAt(bill);

  const [customerName, setCustomerName] = useState(bill?.customerName || '');
  const [customerMobileNo, setCustomerMobileNo] = useState(bill?.customerMobileNo || '');
  const [schoolName, setSchoolName] = useState(bill?.schoolName || '');
  const [paymentMode, setPaymentMode] = useState(
    PAYMENT_MODES.includes(bill?.paymentMode) ? bill.paymentMode : 'Cash'
  );
  const [globalDiscount, setGlobalDiscount] = useState(toInt(bill?.discount));
  const [split, setSplit] = useState({ cash: 0, upi: 0, card: 0 });
  const [sendOnWhatsApp, setSendOnWhatsApp] = useState(false);
  const [lines, setLines] = useState(() =>
    (bill?.bill || [])
      .filter((item) => !item.itemStatus || item.itemStatus === 'NEW')
      .map((item) => lineFromBillItem(item, billHadGlobalDiscount))
  );

  const [schools, setSchools] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCategory, setSearchCategory] = useState('ITEM_CODE');
  const [results, setResults] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const [searching, setSearching] = useState(false);

  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [printInfo, setPrintInfo] = useState(null);
  const searchInputRef = useRef(null);

  const msLeft = getEditMsLeft(bill, now);

  let blockedReason = '';
  if (bill?.status !== 'FRESH') {
    blockedReason = 'Only fresh bills can be updated.';
  } else if (!hasOnlyNewItems(bill)) {
    blockedReason = 'This bill has return or exchange items, so it can’t be edited here.';
  } else if (msLeft <= 0) {
    blockedReason = `The ${BILL_EDIT_WINDOW_MINUTES}-minute edit window for this bill has ended.`;
  }
  const canEdit = !blockedReason;

  // Countdown
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // School suggestions
  useEffect(() => {
    if (!storeId) return;
    axios
      .get(`${API_BASE_URL}/user/filter/getSchoolNameandCode`, { params: { storeId } })
      .then((response) => setSchools(Array.isArray(response.data) ? response.data : []))
      .catch((err) => console.error('Error fetching schools:', err));
  }, [storeId]);

  // Debounced item search (same endpoint as the New Bill screen)
  useEffect(() => {
    const term = searchTerm.trim();
    if (!term) {
      setResults([]);
      setHighlight(-1);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/inventory/getAllItems`, {
          params: { searchTerm: term, searchCategory, storeId },
        });
        setResults(Array.isArray(response.data) ? response.data.slice(0, 50) : []);
        setHighlight(-1);
      } catch (err) {
        console.error('Error searching items:', err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, searchCategory, storeId]);

  // Any change after "Save changes" goes back to editing
  useEffect(() => {
    setConfirming(false);
  }, [lines, customerName, customerMobileNo, schoolName, paymentMode, globalDiscount, split, sendOnWhatsApp]);

  // Escape: close the results first, then the modal
  useEffect(() => {
    if (printInfo) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || submitting) return;
      if (results.length > 0) {
        setResults([]);
        setHighlight(-1);
      } else {
        onClose?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [printInfo, results.length, submitting, onClose]);

  const totals = useMemo(() => {
    const discountPct = toInt(globalDiscount);
    const gross = lines.reduce((sum, line) => sum + getLineAmount(line, discountPct), 0);
    const quantity = lines.reduce((sum, line) => sum + toInt(line.quantity), 0);
    const discountAmount = discountPct > 0 ? roundToFive(Math.trunc((gross * discountPct) / 100)) : 0;
    const net = gross - discountAmount;
    const final = roundToFive(net);
    return { gross, quantity, discountAmount, roundOff: final - net, final };
  }, [lines, globalDiscount]);

  /* ---------------- Line editing ---------------- */
  const updateLine = (key, changes) =>
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...changes } : line)));

  const changeQuantity = (key, delta) =>
    setLines((prev) =>
      prev.map((line) =>
        line.key === key ? { ...line, quantity: Math.max(1, toInt(line.quantity) + delta) } : line
      )
    );

  const removeLine = (key) => setLines((prev) => prev.filter((line) => line.key !== key));

  const addInventoryItem = (item) => {
    if (!item?.itemBarcodeID) return;

    setLines((prev) => {
      const index = prev.findIndex((line) => !line.isCustom && line.barcodeId === item.itemBarcodeID);
      if (index > -1) {
        return prev.map((line, i) =>
          i === index
            ? { ...line, quantity: toInt(line.quantity) + 1, discountAllowed: item.discount === 'Yes' }
            : line
        );
      }
      return [...prev, lineFromInventoryItem(item)];
    });

    // Same school auto-fill as the New Bill screen
    if (
      !schoolName &&
      item.itemCategory &&
      item.itemCategory !== 'GENERAL' &&
      item.itemCategory !== 'WINTER' &&
      schools.some((school) => school.schoolName === item.itemCategory)
    ) {
      setSchoolName(item.itemCategory);
    }

    setError('');
    setSearchTerm('');
    setResults([]);
    setHighlight(-1);
    searchInputRef.current?.focus();
  };

  const addCustomLine = () =>
    setLines((prev) => [
      ...prev,
      {
        key: nextLineKey(),
        barcodeId: CUSTOM_BARCODE,
        description: '',
        itemType: '',
        itemColor: '',
        itemCategory: '',
        itemSize: '',
        price: 0,
        discount: 0,
        discountAllowed: false,
        quantity: 1,
        isCustom: true,
      },
    ]);

  // Enter on a scanned barcode that isn't in the search results
  const lookupBarcode = async (code) => {
    setSearching(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/user/search/item_code`, {
        params: { barcode: code, storeId },
      });
      if (response.data?.itemBarcodeID) {
        addInventoryItem(response.data);
      } else {
        setError(`No item found for “${code}”.`);
      }
    } catch (err) {
      console.error('Error fetching item by barcode:', err);
      setError(`No item found for “${code}”.`);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'ArrowDown' && results.length > 0) {
      event.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (event.key === 'ArrowUp' && results.length > 0) {
      event.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (highlight >= 0 && results[highlight]) addInventoryItem(results[highlight]);
      else if (results.length === 1) addInventoryItem(results[0]);
      else if (searchTerm.trim()) lookupBarcode(searchTerm.trim());
    }
  };

  /* ---------------- Save ---------------- */
  const validate = () => {
    if (blockedReason) return blockedReason;
    if (lines.length === 0) return 'Add at least one item to the bill.';
    if (lines.some((line) => line.isCustom && (!line.description.trim() || toInt(line.price) <= 0))) {
      return 'Custom items need a description and a price.';
    }
    const discountPct = toInt(globalDiscount);
    if (discountPct < 0 || discountPct > 100) return 'Bill discount must be between 0 and 100%.';
    if (sendOnWhatsApp && !MOBILE_PATTERN.test(customerMobileNo)) {
      return 'Enter a valid 10-digit mobile number to send the bill on WhatsApp.';
    }
    if (paymentMode === 'Due' && (!customerName.trim() || !customerMobileNo.trim())) {
      return "Customer name and mobile number are required for 'Due' payment.";
    }
    if (discountPct > 0 && !customerName.trim()) return 'Customer name is required when a discount is applied.';
    if (paymentMode === 'Partial') {
      const parts = [split.cash, split.upi, split.card].map(toInt);
      if (parts.filter((value) => value > 0).length < 2) {
        return 'Use at least two payment methods for a partial payment.';
      }
      const paid = parts.reduce((sum, value) => sum + value, 0);
      if (paid !== totals.final) {
        return `Partial payments add up to ₹${formatAmount(paid)}, but the bill total is ₹${formatAmount(totals.final)}.`;
      }
    }
    return '';
  };

  const handleReview = () => {
    const message = validate();
    if (message) {
      setError(message);
      return;
    }
    setError('');
    setConfirming(true);
  };

  const handleConfirm = async () => {
    const message = validate();
    if (message) {
      setError(message);
      setConfirming(false);
      return;
    }

    const storeConfig = safeParse(sessionStorage.getItem('storeConfig'), {});
    const printType = (storeConfig.printType || 'PDF').trim().toUpperCase();

    const billCreationModel = {
      user,
      token,
      billNo: bill.billNo,
      fy: bill.fy,
      storeId,
      customerName,
      customerMobileNo,
      paymentMode,
      schoolName,
      globalDiscount: toInt(globalDiscount),
      // Regular items are priced from inventory by barcode; custom items carry
      // their own price and descriptive fields.
      itemPurchaseList: lines.map((line) => ({
        barcodeId: line.barcodeId,
        quantity: toInt(line.quantity),
        discount: toInt(line.discount),
        price: line.isCustom ? toInt(line.price) : null,
        description: line.isCustom ? line.description.trim() : null,
        itemCategory: line.isCustom ? line.itemCategory || schoolName : null,
        itemColor: line.isCustom ? line.itemColor : null,
        itemType: line.isCustom ? line.itemType : null,
        // Cloth keeps its length so the backend can price it as rate × metres.
        size: line.isCustom || (line.itemType || '').toUpperCase() === 'CLOTH'
          ? String(line.itemSize ?? '')
          : null,
        storeId,
      })),
      itemReturnList: [],
      transactionModel:
        paymentMode === 'Partial'
          ? { cash: toInt(split.cash), upi: toInt(split.upi), card: toInt(split.card) }
          : { cash: 0, upi: 0, card: 0 },
    };

    setSubmitting(true);
    setError('');
    try {
      const { isThermal, data } = await updateBillApi({
        billCreationModel,
        printType,
        billSendType: sendOnWhatsApp ? 'WHATSAPP' : 'PRINT',
      });

      if (isThermal) {
        if (data && typeof data === 'object') {
          setPrintInfo(data); // onUpdated runs when the print dialog closes
        } else {
          onUpdated?.();
        }
        return;
      }

      const pdfUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      printJS({ printable: pdfUrl, type: 'pdf' });
      onUpdated?.();
    } catch (err) {
      console.error('Error updating bill:', err);
      setError(getUpdateBillErrorMessage(err));
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Thermal reprint replaces the editor once the bill is saved
  if (printInfo) {
    return (
      <PrintBillModal
        isOpen
        onClose={() => {
          setPrintInfo(null);
          onUpdated?.();
        }}
        billInfo={printInfo}
      />
    );
  }

  if (!bill) return null;

  const discountPct = toInt(globalDiscount);
  const timerClass = msLeft <= 0 ? styles.timerExpired : msLeft <= WARN_MS ? styles.timerWarn : '';

  return createPortal(
    <div className={styles.overlay} onClick={() => !submitting && onClose?.()}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bill-update-title"
        onClick={(event) => event.stopPropagation()}
      >
        {/* -------------------- Header -------------------- */}
        <div className={styles.header}>
          <span className={styles.headerIcon} aria-hidden="true">
            <Receipt size={20} />
          </span>
          <div className={styles.headerText}>
            <h2 id="bill-update-title" className={styles.title}>
              Update bill #{bill.billNo}
            </h2>
            <p className={styles.subtitle}>
              {createdAt
                ? `Billed at ${createdAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`
                : 'Billing time unknown'}
              {bill.fy ? ` · FY ${bill.fy}` : ''} · Current total ₹{formatAmount(bill.final_amount)}
            </p>
          </div>
          <div className={styles.headerRight}>
            <span className={`${styles.timer} ${timerClass}`} title={`Bills can be edited for ${BILL_EDIT_WINDOW_MINUTES} minutes after billing`}>
              <Clock size={14} aria-hidden="true" />
              {msLeft > 0 ? `${formatTimeLeft(msLeft)} left` : 'Time up'}
            </span>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={onClose}
              disabled={submitting}
              aria-label="Close"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* -------------------- Body -------------------- */}
        <div className={styles.body}>
          {blockedReason && (
            <div className={`${styles.banner} ${styles.bannerWarn}`} role="alert">
              <TriangleAlert size={16} aria-hidden="true" />
              <span>{blockedReason}</span>
            </div>
          )}
          {error && error !== blockedReason && (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
              <TriangleAlert size={16} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <fieldset className={styles.fieldset} disabled={!canEdit || submitting}>
            <div className={styles.topGrid}>
              {/* Customer */}
              <section className={styles.panel} aria-labelledby="bill-update-customer">
                <h3 id="bill-update-customer" className={styles.panelTitle}>
                  <UserRound size={15} aria-hidden="true" />
                  Customer
                </h3>
                <div className={styles.fieldGrid}>
                  <div className={styles.field}>
                    <label htmlFor="bill-update-name" className={styles.label}>Customer name</label>
                    <div className={styles.inputWrap}>
                      <UserRound size={15} className={styles.inputIcon} aria-hidden="true" />
                      <input
                        id="bill-update-name"
                        type="text"
                        className={styles.input}
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="bill-update-mobile" className={styles.label}>Mobile number</label>
                    <div className={styles.inputWrap}>
                      <Phone size={15} className={styles.inputIcon} aria-hidden="true" />
                      <input
                        id="bill-update-mobile"
                        type="text"
                        inputMode="tel"
                        className={styles.input}
                        value={customerMobileNo}
                        onChange={(e) => setCustomerMobileNo(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={`${styles.field} ${styles.fieldFull}`}>
                    <label htmlFor="bill-update-school" className={styles.label}>School</label>
                    <div className={styles.inputWrap}>
                      <School size={15} className={styles.inputIcon} aria-hidden="true" />
                      <input
                        id="bill-update-school"
                        type="text"
                        list="bill-update-schools"
                        className={styles.input}
                        value={schoolName}
                        onChange={(e) => setSchoolName(e.target.value)}
                        placeholder="Select or type a school"
                      />
                      <datalist id="bill-update-schools">
                        {schools.map((school) => (
                          <option key={`${school.schoolName}-${school.schoolCode}`} value={school.schoolName}>
                            {school.schoolCode}
                          </option>
                        ))}
                      </datalist>
                    </div>
                  </div>
                </div>
              </section>

              {/* Payment */}
              <section className={styles.panel} aria-labelledby="bill-update-payment">
                <h3 id="bill-update-payment" className={styles.panelTitle}>
                  <Wallet size={15} aria-hidden="true" />
                  Payment
                </h3>
                <div className={styles.fieldGrid}>
                  <div className={styles.field}>
                    <label htmlFor="bill-update-mode" className={styles.label}>Payment mode</label>
                    <select
                      id="bill-update-mode"
                      className={styles.select}
                      value={paymentMode}
                      onChange={(e) => setPaymentMode(e.target.value)}
                    >
                      {PAYMENT_MODES.map((mode) => (
                        <option key={mode} value={mode}>{PAYMENT_LABELS[mode]}</option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="bill-update-discount" className={styles.label}>Bill discount (%)</label>
                    <input
                      id="bill-update-discount"
                      type="number"
                      min="0"
                      max="100"
                      className={`${styles.input} ${styles.inputPlain}`}
                      value={globalDiscount}
                      onChange={(e) => setGlobalDiscount(e.target.value)}
                    />
                  </div>

                  {paymentMode === 'Partial' && (
                    <div className={`${styles.fieldFull} ${styles.splitGrid}`}>
                      {[
                        { key: 'cash', label: 'Cash' },
                        { key: 'upi', label: 'UPI' },
                        { key: 'card', label: 'Card' },
                      ].map(({ key, label }) => (
                        <div key={key} className={styles.field}>
                          <label htmlFor={`bill-update-split-${key}`} className={styles.label}>{label}</label>
                          <input
                            id={`bill-update-split-${key}`}
                            type="number"
                            min="0"
                            className={`${styles.input} ${styles.inputPlain}`}
                            value={split[key]}
                            onChange={(e) => setSplit((prev) => ({ ...prev, [key]: e.target.value }))}
                          />
                        </div>
                      ))}
                      <p className={styles.hint}>
                        Enter the new split — it must add up to ₹{formatAmount(totals.final)}.
                      </p>
                    </div>
                  )}

                
                </div>
              </section>
            </div>

            {/* Items */}
            <section className={`${styles.panel} ${styles.itemsPanel}`} aria-labelledby="bill-update-items">
              <div className={styles.itemsHeader}>
                <h3 id="bill-update-items" className={styles.panelTitle}>
                  <Receipt size={15} aria-hidden="true" />
                  Items
                  <span className={styles.countBadge}>{lines.length}</span>
                </h3>

                <div className={styles.itemsTools}>
                  <div className={styles.searchWrap}>
                    <Search size={15} className={styles.searchIcon} aria-hidden="true" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      className={styles.searchInput}
                      placeholder={
                        searchCategory === 'ITEM_CODE'
                          ? 'Scan barcode or search item code'
                          : 'Search by description'
                      }
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      aria-label="Add item"
                      role="combobox"
                      aria-autocomplete="list"
                      aria-controls="bill-update-results"
                      aria-expanded={results.length > 0}
                      autoComplete="off"
                    />
                    {searching && <span className={styles.searchSpinner} aria-hidden="true" />}

                    {results.length > 0 && (
                      <div id="bill-update-results" className={styles.results} role="listbox">
                        {results.map((item, index) => (
                          <button
                            key={item.itemBarcodeID}
                            type="button"
                            role="option"
                            aria-selected={index === highlight}
                            className={`${styles.resultRow} ${index === highlight ? styles.resultRowOn : ''}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseEnter={() => setHighlight(index)}
                            onClick={() => addInventoryItem(item)}
                          >
                            <span className={styles.resultCode}>{item.itemCode}</span>
                            <span className={styles.resultDesc}>{item.description}</span>
                            <span className={styles.resultMeta}>
                              {[item.itemType, item.itemColor, item.itemSize].filter(Boolean).join(' · ')}
                            </span>
                            <span className={styles.resultPrice}>₹{formatAmount(item.price)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnGhost}`}
                    onClick={() => setSearchCategory((c) => (c === 'ITEM_CODE' ? 'DESCRIPTION' : 'ITEM_CODE'))}
                    title="Change what the search looks at"
                  >
                    {SEARCH_CATEGORY_LABELS[searchCategory]}
                  </button>
                  <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={addCustomLine}>
                    <Plus size={15} aria-hidden="true" />
                    Custom item
                  </button>
                </div>
              </div>

              <div className={styles.tableWrap}>
                {lines.length > 0 ? (
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item</th>
                        <th>Details</th>
                        <th className={styles.num}>Price</th>
                        <th className={styles.center}>Disc %</th>
                        <th className={styles.center}>Qty</th>
                        <th className={styles.num}>Amount</th>
                        <th aria-label="Remove" />
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, index) => {
                        const amount = getLineAmount(line, discountPct);
                        const discountEditable = !line.isCustom && discountPct === 0 && line.discountAllowed !== false;
                        return (
                          <tr key={line.key}>
                            <td className={styles.rowIndex}>{index + 1}</td>
                            <td>
                              {line.isCustom ? (
                                <input
                                  type="text"
                                  className={styles.cellInput}
                                  placeholder="Custom item description"
                                  value={line.description}
                                  onChange={(e) => updateLine(line.key, { description: e.target.value })}
                                  aria-label={`Description for item ${index + 1}`}
                                />
                              ) : (
                                <>
                                  <span className={styles.itemName}>{line.description || line.barcodeId}</span>
                                  <span className={styles.itemSub}>{line.barcodeId}</span>
                                </>
                              )}
                            </td>
                            <td>
                              {line.isCustom ? (
                                <input
                                  type="text"
                                  className={`${styles.cellInput} ${styles.cellInputShort}`}
                                  placeholder="Size"
                                  value={line.itemSize}
                                  onChange={(e) => updateLine(line.key, { itemSize: e.target.value })}
                                  aria-label={`Size for item ${index + 1}`}
                                />
                              ) : (
                                <span className={styles.muted}>
                                  {[line.itemType, line.itemColor, line.itemSize].filter(Boolean).join(' · ') || '—'}
                                </span>
                              )}
                            </td>
                            <td className={styles.num}>
                              {line.isCustom ? (
                                <input
                                  type="number"
                                  min="0"
                                  className={`${styles.cellInput} ${styles.cellInputNum}`}
                                  value={line.price}
                                  onChange={(e) => updateLine(line.key, { price: toInt(e.target.value) })}
                                  aria-label={`Price for item ${index + 1}`}
                                />
                              ) : (
                                formatAmount(line.price)
                              )}
                            </td>
                            <td className={styles.center}>
                              {discountEditable ? (
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  className={`${styles.cellInput} ${styles.cellInputDisc}`}
                                  value={line.discount}
                                  onChange={(e) =>
                                    updateLine(line.key, {
                                      discount: Math.min(100, Math.max(0, toInt(e.target.value))),
                                    })
                                  }
                                  aria-label={`Discount for item ${index + 1}`}
                                  title="Applied only to items that allow discounts"
                                />
                              ) : (
                                <span className={styles.muted}>{discountPct > 0 ? `${discountPct}%` : '—'}</span>
                              )}
                            </td>
                            <td className={styles.center}>
                              <div className={styles.stepper}>
                                <button
                                  type="button"
                                  className={styles.stepperBtn}
                                  onClick={() => changeQuantity(line.key, -1)}
                                  aria-label={`Decrease quantity of item ${index + 1}`}
                                >
                                  −
                                </button>
                                <input
                                  type="number"
                                  min="1"
                                  className={styles.stepperValue}
                                  value={line.quantity}
                                  onChange={(e) => updateLine(line.key, { quantity: Math.max(1, toInt(e.target.value)) })}
                                  aria-label={`Quantity of item ${index + 1}`}
                                />
                                <button
                                  type="button"
                                  className={styles.stepperBtn}
                                  onClick={() => changeQuantity(line.key, 1)}
                                  aria-label={`Increase quantity of item ${index + 1}`}
                                >
                                  +
                                </button>
                              </div>
                            </td>
                            <td className={`${styles.num} ${styles.amountCell}`}>{formatAmount(amount)}</td>
                            <td className={styles.center}>
                              <button
                                type="button"
                                className={styles.removeBtn}
                                onClick={() => removeLine(line.key)}
                                aria-label={`Remove item ${index + 1}`}
                                title="Remove"
                              >
                                <Trash2 size={15} aria-hidden="true" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className={styles.empty}>
                    <p className={styles.emptyTitle}>No items on this bill</p>
                    <p className={styles.emptyText}>Scan a barcode or search above to add items.</p>
                  </div>
                )}
              </div>
            </section>
          </fieldset>
        </div>

        {/* -------------------- Footer -------------------- */}
        <div className={styles.footer}>
          <dl className={styles.totals}>
            <div className={styles.totalItem}>
              <dt>Qty</dt>
              <dd>{totals.quantity}</dd>
            </div>
            <div className={styles.totalItem}>
              <dt>Gross</dt>
              <dd>₹{formatAmount(totals.gross)}</dd>
            </div>
            {totals.discountAmount > 0 && (
              <div className={styles.totalItem}>
                <dt>Discount</dt>
                <dd>−₹{formatAmount(totals.discountAmount)}</dd>
              </div>
            )}
            <div className={styles.totalItem}>
              <dt>Round off</dt>
              <dd>{totals.roundOff >= 0 ? '+' : '−'}₹{formatAmount(Math.abs(totals.roundOff))}</dd>
            </div>
            <div className={`${styles.totalItem} ${styles.totalMain}`}>
              <dt>New total</dt>
              <dd>₹{formatAmount(totals.final)}</dd>
            </div>
          </dl>

          <div className={styles.footerActions}>
            {confirming ? (
              <>
                <span className={styles.confirmText}>
                  Replace bill #{bill.billNo}? Total ₹{formatAmount(bill.final_amount)} → ₹{formatAmount(totals.final)}.
                  Stock and payments are recalculated.
                </span>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnGhost}`}
                  onClick={() => setConfirming(false)}
                  disabled={submitting}
                >
                  <ArrowLeft size={15} aria-hidden="true" />
                  Back
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handleConfirm}
                  disabled={submitting || !canEdit}
                >
                  {submitting ? (
                    <>
                      <span className={styles.btnSpinner} aria-hidden="true" />
                      Updating…
                    </>
                  ) : (
                    <>
                      <Check size={15} aria-hidden="true" />
                      Confirm &amp; reprint
                    </>
                  )}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnGhost}`}
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handleReview}
                  disabled={!canEdit}
                >
                  Save changes
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BillUpdateModal;
